import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  ConfigProvider,
  Flex,
  Form,
  message,
  Modal,
  Select,
  Typography,
} from "antd";
import { useForm } from "antd/es/form/Form";
import {
  ModalHeader,
  modalButtonsTheme,
  modalSubtitleStyle,
  outlinedButtonTheme,
} from "components/modals/ModalChrome";
import { useAddToWaitlistV2 } from "hooks/mutations/waitlist-v2";
import { AddToWaitlistForm } from "hooks/api/waitlist-v2";
import WindowConflictModal, {
  WindowConflictHint,
} from "components/modals/WindowConflictModal.component";
import { WindowConflict } from "shared/types";
import { getCustomerPackageByClassCustomerId } from "hooks/queries/customerpackage";
import { availableCustomersByScheduleV2Options } from "hooks/queries/schedule-v2";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import {
  personFilterOption,
  renderPersonOption,
} from "shared/components/PersonSelectOption";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { creditCostSuffix, getActualRemainingCredits, PRODUCT_TYPE_LABELS, SHARED_LABEL } from "shared/utils";
import { DEFAULT_DATE_SUBMIT_FORMAT, tzDate } from "shared/utils/dayjs";
import { buildCustomerPickResumeDraft } from "shared/utils/scheduleResumeDraft";
import { textPx } from "shared/constants/zoom";

interface AddToWaitlistModalProps {
  scheduleId: string;
  occurrenceTimestamp: string;
  classId: string;
  confirmedCount: number;
  capacity: number;
  waitlistLength: number;
  disabled: boolean;
  onAdded?: () => void;
  // Reopen with this customer pre-selected after a round trip to their detail
  // page to buy a package (see the "Kelola Paket Klien" empty-state below) —
  // set by ScheduleDetails.tsx's resume effect, consumed once then cleared.
  resumeCustomerId?: string;
  onResumeConsumed?: () => void;
}

const AddToWaitlistModal = ({
  scheduleId,
  occurrenceTimestamp,
  classId,
  confirmedCount,
  capacity,
  waitlistLength,
  disabled,
  onAdded,
  resumeCustomerId,
  onResumeConsumed,
}: AddToWaitlistModalProps) => {
  const navigate = useNavigate();
  const [form] = useForm();
  const [isOpen, setOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<string>();
  const { data: customers, isLoading: customerLoading } = useQuery(
    availableCustomersByScheduleV2Options(
      { schedule_id: scheduleId, occurrence_timestamp: occurrenceTimestamp },
      { enabled: isOpen && !disabled },
    ),
  );
  const { data: customerPackages, isLoading: customerPackagesLoading } =
    getCustomerPackageByClassCustomerId(
      {
        customer_id: selectedCustomer,
        class_id: classId,
        // Packages valid for THIS class's date, like NewBookingModal: the
        // server checks the package against the class date, so judging by
        // today offered packages that expire before the class and hid one
        // on its last valid day.
        occurrence_date: tzDate(occurrenceTimestamp).format(
          DEFAULT_DATE_SUBMIT_FORMAT,
        ),
        // See NewBookingModal: a package the class would cut short is offered,
        // marked, and opens the swap / extend choice.
        include_conflicts: "true",
      },
      { enabled: isOpen },
    );
  // A package the class would cut short: the refusal carries the conflict,
  // and the admin's swap / extend choice retries with on_conflict.
  const [conflict, setConflict] = useState<{
    conflict: WindowConflict;
    form: AddToWaitlistForm;
  } | null>(null);
  const { mutate: addToWaitlist, isSuccess } = useAddToWaitlistV2((c, form) =>
    setConflict({ conflict: c, form }),
  );

  // Resuming after a round trip to a client's detail page to buy a package
  // (see handleBuyPackage below) — reopen with that customer pre-selected.
  useEffect(() => {
    if (!resumeCustomerId) return;
    setOpen(true);
    setSelectedCustomer(resumeCustomerId);
    form.setFieldValue("customer_id", resumeCustomerId);
    onResumeConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeCustomerId]);

  // A customer with no valid package for this class can't be waitlisted from
  // here — send the admin to manage their packages, carrying enough state to
  // reopen this modal with them pre-selected on return (see the resume
  // effect above and ScheduleDetails.tsx's resume effect, which threads the
  // draft down through PrimaryAction).
  const handleBuyPackage = (customerId: string) => {
    navigate(`${PATHS.CUSTOMER}/${customerId}`, {
      state: {
        scheduleDraft: buildCustomerPickResumeDraft(
          "add-waitlist",
          `${PATHS.SCHEDULE}/${scheduleId}?occurrence_timestamp=${encodeURIComponent(occurrenceTimestamp)}`,
          customerId,
        ),
      },
    });
  };

  const handleExit = () => {
    setOpen(false);
    form.resetFields();
    setSelectedCustomer(undefined);
  };

  useEffect(() => {
    if (isSuccess) {
      handleExit();
      onAdded?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        addToWaitlist({
          customer_id: values.customer_id,
          customer_package_id: values.customer_package_id,
          schedule_id: scheduleId,
          occurrence_timestamp: occurrenceTimestamp,
        });
      })
      .catch(() => {
        message.error(translate("messages.error.invalid_form"));
      });
  };

  return (
    <>
      {/* Blue (navy) primary, matching the schedule create CTA. */}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setOpen(true)}
          disabled={disabled}
        >
          {translate("button.add_to_waitlist")}
        </Button>
      <Modal
        title={<ModalHeader title={translate("button.add_to_waitlist")} />}
        open={isOpen}
        onCancel={handleExit}
        // Popup button system: solid-ink Batal dismiss, navy confirm.
        footer={
          <ConfigProvider theme={modalButtonsTheme}>
            <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
              <Button onClick={handleExit}>{translate("button.cancel")}</Button>
              <Button type="primary" onClick={onSubmit}>
                {translate("button.add")}
              </Button>
            </Flex>
          </ConfigProvider>
        }
      >
        <Typography.Text style={modalSubtitleStyle}>
          {/* Mid-sentence values, so i18n.t with interpolation rather than the
              options-less translate() cast. */}
          {translateWithVars("schedule.waitlist_full_note", {
            confirmed: confirmedCount,
            capacity,
            position: waitlistLength + 1,
          })}
        </Typography.Text>
        <Form
          form={form}
          layout="vertical"
          name="addToWaitlistForm"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="customer_id"
            label={translate("customer")}
            rules={[
              {
                required: true,
                message: `${translate("form.error.select")} ${translate("customer")}`,
              },
            ]}
          >
            <Select
              placeholder={`${translate("form.placeholders.select")} ${translate("customer")}`}
              loading={customerLoading}
              onChange={(value: string) => {
                setSelectedCustomer(value);
                // Clear the previous customer's package so it can't be
                // submitted paired with a different customer.
                form.setFieldValue("customer_package_id", undefined);
              }}
              showSearch
              filterOption={personFilterOption}
              optionRender={renderPersonOption}
              options={customers?.map((customer) => ({
                value: customer.id,
                label: customer.full_name,
                phone: customer.phone_number,
              }))}
              notFoundContent={translate(
                "form.error.empty.available_customer_booking",
              )}
            />
          </Form.Item>
          <Form.Item
            name="customer_package_id"
            label={translate("label.customer_package")}
            rules={[
              {
                required: true,
                message: `${translate("form.error.select")} ${translate("label.customer_package")}`,
              },
            ]}
          >
            <Select
              placeholder={`${translate("form.placeholders.select")} ${translate("label.customer_package")}`}
              loading={customerPackagesLoading}
              optionLabelProp="label"
              // Also disabled once we know there's nothing to pick (empty,
              // done loading) — otherwise it renders as a live, clickable
              // field right above the "no valid package" notice below.
              disabled={
                !selectedCustomer ||
                (!customerPackagesLoading && customerPackages?.length === 0)
              }
            >
              {customerPackages && customerPackages.length > 0 ? (
                customerPackages.map((pkg) => (
                  <Select.Option
                    key={pkg.id}
                    value={pkg.id}
                    // The field shows just the package; the conflict note
                    // stays in the open list.
                    label={
                      pkg.package_deal.unlimited
                        ? pkg.package_deal.name
                        : `${pkg.package_deal.name} - [${translate("customer_package.remaining")}: ${getActualRemainingCredits(pkg)}/${pkg.total_credits}]${creditCostSuffix(pkg)}`
                    }
                  >
                    {/* A shared package is otherwise indistinguishable from
                        the client's own, and it spends someone else's credits,
                        so the owner is named. */}
                    {pkg.is_shared_with_me && (
                      <span style={{ marginRight: 8 }}>
                        <StatusBadge
                          tone="progress"
                          label={SHARED_LABEL}
                          dot={false}
                        />
                      </span>
                    )}
                    {pkg.package_deal.unlimited ? (
                      <>
                        {pkg.package_deal.name}
                        <span style={{ marginLeft: 8 }}>
                          <StatusBadge tone="navy" label={PRODUCT_TYPE_LABELS.membership} dot={false} />
                        </span>
                      </>
                    ) : (
                      `${pkg.package_deal.name} - [${translate("customer_package.remaining")}: ${getActualRemainingCredits(pkg)}/${pkg.total_credits}]${creditCostSuffix(pkg)}`
                    )}
                    <WindowConflictHint pkg={pkg} />
                  </Select.Option>
                ))
              ) : (
                <Select.Option disabled>
                  {translate("form.empty.customer_package")}
                </Select.Option>
              )}
            </Select>
          </Form.Item>
          {selectedCustomer && !customerPackagesLoading && customerPackages?.length === 0 && (
            <div
              style={{
                marginTop: -4,
                padding: "12px 14px",
                borderRadius: 12,
                background: colors.bgSoft,
                border: `1px solid ${colors.line}`,
              }}
            >
              <Typography.Text
                style={{ display: "block", fontSize: textPx(13), color: colors.ink2, marginBottom: 10 }}
              >
                {translate("schedule.drawer.no_valid_package")}
              </Typography.Text>
              {/* This modal's outer ConfigProvider (modalButtonsTheme) makes
                  default buttons solid ink for the footer's Batal —
                  outlinedButtonTheme restores the app's normal outlined
                  button (incl. hover/active) for this secondary action, so
                  it matches ClientPackageSection.tsx's. */}
              <ConfigProvider theme={outlinedButtonTheme}>
                <Button size="small" onClick={() => handleBuyPackage(selectedCustomer)}>
                  {translate("schedule.drawer.manage_client_packages")}
                </Button>
              </ConfigProvider>
            </div>
          )}
        </Form>
      </Modal>
      <WindowConflictModal
        conflict={conflict?.conflict ?? null}
        onCancel={() => setConflict(null)}
        onChoose={(choice) => {
          if (conflict) addToWaitlist({ ...conflict.form, on_conflict: choice });
          setConflict(null);
        }}
      />
    </>
  );
};

export default AddToWaitlistModal;
